import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Column, Row } from 'hedron';
import isEqual from 'lodash/isEqual';
import Router from 'next/router';
import Dynamic from 'next/dynamic';
import { Wrapper } from '../css_global/utils.css';
import { Accordion, AccordionGroup } from '../../components_common/Accordion';
import CategoryFilter from '../../components_common/Filters/CategoryFilter';
import SeasonsFilter from '../../components_common/Filters/SeasonsFilter';
import SizesFilter from '../../components_common/Filters/SizesFilter';
import PriceFilter from '../../components_common/Filters/PriceFilter';
import NoResults from '../../components_common/NoResults';
import RefineBySection from '../../components_common/RefineBySection';
import SearchKeyword from '../../components_common/SearchKeyword';
import SearchResults from '../../components_common/SearchResults';
import SearchDescription from '../../components/SearchDescription';
import Breadcrumbs from '../components/Breadcrumbs';
import FiltersForm from '../components/FiltersForm';
import withCountryCode from '../../components/RoutesHoc/RoutesHoc';
import ProductsHeader from '../components/ProductsHeader';
import { SizeGuide, SizeGuideLabel } from '../../components/SizeGuide';
import { ProductListMicrodata } from '../../components_common/Microdata';
import {
  easeInOutQuint,
  getProductsViewedData,
  PAGE_BRAND_DETAIL,
  ReactPixel,
  scrollToY,
  searchTreeForCategoryWithSizes,
  separateProductDataIntoRows,
  track,
  onSaleParam,
} from '../../helpers';
import { generateProductRows } from '../../helpers/productRows';
import { buildSearchProductsFromUrl, isShoesCategory } from '../../helpers/search_helpers';
import {
  callBackend,
  changeOffset,
  clearFilters,
  filterBrands,
  filterCategories,
  filterPrice,
  filterSeasons,
  filterSizes,
  sort,
} from '../../common/actions';
import Pagination from '../components/SortAndPagination/Pagination';
import { FilterTypes, SeasonData } from '../../common/models';
import { allModes, designerMode, productMode, storeMode } from '../../helpers/productListModes';
import { fdSet } from '../../helpers/fdHelper';
import { SizeData } from '../../common/models/SizeData';
import TrackingEvents from '../../common/models/TrackingEvents';
import { sanitizeBrandDescription } from '../../components/ProductList/sanitizers';
import { normalizeCategoryDescription } from '../../components/ProductList/normalizers';

const BrandsFilter = Dynamic(import('../../components_common/Filters/BrandsFilter'));
const ROWS_TO_LOAD_EAGERLY_COUNT = 2;

const mapStateToProps = ({
  countryCode,
  api: {
    basePathAdmin,
    isAdmin,
    filterSettings,
    fbPixelSearchDetailData,
    description,
    brandName,
    countryId,
    currencySymbol,
    categoryName,
    categoryPath,
    categoryUrl,
    location,
    offset,
    products,
    sortFilterSelect,
    s3BasePath,
    sizes,
    total,
  },
  department,
}) => ({
  countryCode,
  basePathAdmin,
  countryId,
  department,
  isAdmin,
  filterSettings,
  fbPixelSearchDetailData,
  description,
  brandName,
  currencySymbol,
  categoryName,
  categoryPath,
  categoryUrl,
  location,
  offset,
  products,
  sortFilterSelect,
  s3BasePath,
  sizes,
  total,
});

const mapDispatchToProps = dispatch => ({
  handleBrandsFilter: (brandsIds, mode) => dispatch(filterBrands(brandsIds, mode)),
  handleCategoriesFilter: (categoryId, categoryUrl, categoryName, mode) =>
    dispatch(filterCategories(categoryId, categoryUrl, categoryName, mode)),
  handleClearFilters: (availableFilters, mode) => dispatch(clearFilters(availableFilters, mode)),
  handlePriceFilter: (priceGte, priceLte, mode) => dispatch(filterPrice(priceGte, priceLte, mode)),
  handleSeasonsFilter: (currSeasons, mode) => dispatch(filterSeasons(currSeasons, mode)),
  handleSizesFilter: (sizes, mode) => dispatch(filterSizes(sizes, mode)),
  handleSorting: (sortKey, sortText, mode) => dispatch(sort(sortKey, sortText, mode)),
  handleOffset: (offset, mode) => dispatch(changeOffset(offset, mode)),
  searchProducts: (url, mode, backUrl) => dispatch(callBackend(url, mode, backUrl)),
});

class ProductList extends React.Component {
  constructor() {
    super();
    this.state = {
      sizeGuideVisible: false,
      filtersOpenIndex: 0,
    };
  }

  componentDidMount() {
    const { searchProducts, mode, query, page, categoryPath, categoryName, brandName } = this.props;
    const productsViewData = getProductsViewedData(categoryPath, categoryName, {
      query,
      isBrand: page === PAGE_BRAND_DETAIL,
      brandName,
    });
    productsViewData.product = { on_sale: window.location.search.slice(-9) === onSaleParam };
    track(TrackingEvents.productsViewed(), productsViewData);

    Router.beforePopState(({ as }) => {
      // manage browser back and forward buttons
      const url = buildSearchProductsFromUrl(as, mode);
      searchProducts(url, mode, as);
      return true;
    });

    fdSet();
  }

  componentDidUpdate(oldProps) {
    const { products } = this.props;
    if (!isEqual(oldProps.products, products)) {
      scrollToY(0, 1500, easeInOutQuint);
      const { categoryPath, categoryName, query, brandName, page } = this.props;
      const productsViewData = getProductsViewedData(categoryPath, categoryName, {
        query,
        isBrand: page === PAGE_BRAND_DETAIL,
        brandName,
      });
      productsViewData.product = { on_sale: window.location.search.slice(-9) === onSaleParam };
      track(TrackingEvents.productsViewed(), productsViewData);
    }
  }

  getFilterType = index => {
    switch (index) {
      case 0:
        return 'category';
      case 1:
        return 'brand';
      case 2:
        return 'seasons';
      case 3:
        return 'sizes';
      default:
        return null;
    }
  };

  handleFiltersExpand = index => {
    const { filtersOpenIndex } = this.state;
    if (filtersOpenIndex === index) {
      this.setState({ filtersOpenIndex: -1 });
      track(TrackingEvents.filterClose(), { ui: { filter_type: this.getFilterType(filtersOpenIndex) } });
    } else {
      this.setState({ filtersOpenIndex: index });
      track(TrackingEvents.filterOpen(), { ui: { filter_type: this.getFilterType(index) } });
    }
  };

  render() {
    const {
      basePathAdmin,
      description,
      brandName,
      department,
      currencySymbol,
      categoryName,
      categoryPath,
      categoryUrl,
      countryCode,
      countryId,
      filterSettings,
      freeShipping,
      location,
      isAdmin,
      offset,
      products,
      query,
      sortFilterSelect,
      s3BasePath,
      total,
      handleBrandsFilter,
      handleCategoriesFilter,
      handleClearFilters,
      handleOffset,
      handlePriceFilter,
      handleSeasonsFilter,
      handleSizesFilter,
      handleSorting,
      mode,
      buildUrlWithCountry,
    } = this.props;

    const { filtersOpenIndex, sizeGuideVisible } = this.state;

    const {
      availableSeasons,
      availableSizes,
      brandsIds,
      availableBrands,
      categoryTree,
      categoryId,
      categoryIdsForBrands,
      departmentIdsForBrands,
      currentSeason,
      seasons,
      sizes,
      storeName,
      storeId,
      priceGte,
      priceLte,
    } = filterSettings || {};

    const productRows = separateProductDataIntoRows(products, 3);

    const searchPage = mode !== designerMode;
    const storePageAndAdmin = mode === storeMode && isAdmin;

    const allFilters = [FilterTypes.category(), FilterTypes.season(), FilterTypes.price()];
    if (searchPage) {
      allFilters.push(FilterTypes.designer());
    }
    if (searchTreeForCategoryWithSizes(categoryTree, categoryId)) {
      allFilters.push(FilterTypes.size());
    }

    let text = null;
    switch (mode) {
      case storeMode:
        text = storeName;
        break;
      case productMode:
        text = categoryName;
        break;
      case designerMode:
        text = brandName;
        break;
    }

    return (
      <>
        <Wrapper padding="0 0 150px 0">
          {sizeGuideVisible ? (
            <SizeGuide
              categoryTree={categoryTree}
              currentCategoryId={categoryId}
              department={department}
              closeSizeGuide={() => this.setState({ sizeGuideVisible: false })}
            />
          ) : null}
          <Breadcrumbs
            department={department}
            navigationPath={categoryPath}
            brandName={searchPage ? null : brandName}
          />
          <ProductsHeader
            text={text}
            textLink={storePageAndAdmin ? `${basePathAdmin}/admin/mkt_store/edit_store/${storeId}` : null}
            totalProducts={total}
            handleOffsetChange={newOffset => handleOffset(newOffset, mode)}
            paginationOffset={offset}
            currentSorting={sortFilterSelect}
            handleSortChange={(...args) => handleSorting(...args, mode)}
            s3BasePath={s3BasePath}
          />
          <Wrapper>
            <Row style={{ minWidth: '1020px' }}>
              <Column md={3} lg={3} style={{ marginTop: 0, padding: 0 }}>
                <FiltersForm
                  id="filters_form"
                  action={buildUrlWithCountry(categoryUrl)}
                  method="get"
                  className="span3"
                  total={total}
                  ref={elem => {
                    this.filterForm = elem;
                  }}
                >
                  <RefineBySection
                    isDesktop
                    currencySymbol={currencySymbol}
                    department={department}
                    availableFilters={allFilters}
                    filterSettings={filterSettings}
                    handleClearFilters={(...args) => {
                      this.setState({ filtersOpenIndex: 0 });
                      handleClearFilters(...args, mode);
                    }}
                    handleSizesFilter={(...args) => handleSizesFilter(...args, mode)}
                    handlePriceFilter={(...args) => handlePriceFilter(...args, mode)}
                    handleSeasonsFilter={(...args) => handleSeasonsFilter(...args, mode)}
                    handleBrandsFilter={(...args) => handleBrandsFilter(...args, mode)}
                    s3BasePath={s3BasePath}
                  />
                  <div id="product_filters">
                    <AccordionGroup
                      iconPosition="right"
                      s3BasePath={s3BasePath}
                      isDesktop
                      handleTriggerClick={this.handleFiltersExpand}
                      openPosition={filtersOpenIndex}
                    >
                      <Accordion title="CATEGORIES" margin="0 0 15px 0">
                        <Row>
                          <Column fluid>
                            <CategoryFilter
                              categoryIdsForBrands={categoryIdsForBrands}
                              departmentIdsForBrands={departmentIdsForBrands}
                              isDesignerMode={mode === designerMode}
                              handleFilterClick={(...args) => handleCategoriesFilter(...args, mode)}
                              filters={categoryTree}
                              currCategoryId={categoryId}
                              isDesktop
                            />
                          </Column>
                        </Row>
                      </Accordion>
                      {searchPage ? (
                        <Accordion title="DESIGNERS" margin="20px 0 15px 0">
                          <BrandsFilter
                            handleFilterClick={(...args) => handleBrandsFilter(...args, mode)}
                            availableBrands={availableBrands}
                            isDesktop
                            selectedBrandsIds={brandsIds}
                          />
                        </Accordion>
                      ) : null}
                      <Accordion title="SEASONS" margin="20px 0 15px 0">
                        <SeasonsFilter
                          availableSeasons={availableSeasons.map(el => SeasonData.fromSeasonString(el))}
                          currentSeason={SeasonData.fromSeasonString(currentSeason)}
                          handleFilterClick={(...args) => handleSeasonsFilter(...args, mode)}
                          isDesktop
                          selectedSeasons={seasons.map(el => SeasonData.fromSeasonString(el))}
                        />
                      </Accordion>
                      {allFilters.indexOf(FilterTypes.size()) > -1 ? (
                        <Accordion title="SIZES" margin="20px 0 15px 0">
                          <SizesFilter
                            department={department}
                            countryId={countryId}
                            handleFilterClick={(...args) => handleSizesFilter(...args, mode)}
                            sizes={SizeData.sizesArrayWithUniversalToString(
                              availableSizes,
                              isShoesCategory(categoryId),
                              department,
                            )}
                            currSizes={SizeData.sizesArrayToString(sizes, department)}
                            isDesktop
                            isShoesCategory={isShoesCategory(categoryId)}
                            s3BasePath={s3BasePath}
                          />
                          <SizeGuideLabel
                            department={department}
                            setSizeGuideVisible={() => {
                              this.setState({ sizeGuideVisible: true });
                            }}
                          />
                        </Accordion>
                      ) : null}
                      <Accordion title="PRICE">
                        <PriceFilter
                          handlePriceFilter={handlePriceFilter}
                          mode={mode}
                          priceGte={priceGte}
                          priceLte={priceLte}
                          currencySymbol={currencySymbol}
                        />
                      </Accordion>
                    </AccordionGroup>
                  </div>
                </FiltersForm>
              </Column>

              <Column md={9} lg={9} style={{ marginBottom: '10px', paddingTop: 0 }}>
                {description && (
                  <SearchDescription
                    data-cy="accordion-description-designer"
                    dangerouslySetInnerHTML={{
                      __html: searchPage
                        ? normalizeCategoryDescription(countryCode, description)
                        : sanitizeBrandDescription(description),
                    }}
                  />
                )}
                <SearchResults isDesktop />
                {query ? <SearchKeyword left margin="32px 0 20px 5px" noResults={total === 0} keyword={query} /> : null}
                {productRows.length ? (
                  generateProductRows(
                    productRows,
                    s3BasePath,
                    currencySymbol,
                    true,
                    freeShipping,
                    ROWS_TO_LOAD_EAGERLY_COUNT,
                    '161px',
                  )
                ) : (
                  <div>{!query ? <NoResults isDesktop /> : null}</div>
                )}
              </Column>
            </Row>
          </Wrapper>

          {productRows.length ? (
            <Pagination
              paginationOffset={offset}
              totalProducts={total}
              handleClick={newOffset => handleOffset(newOffset, mode)}
            />
          ) : null}
        </Wrapper>
        <ProductListMicrodata products={products} numberOfItems={total} {...location} />
      </>
    );
  }
}

ProductList.propTypes = {
  availableSeasons: PropTypes.arrayOf(PropTypes.string),
  availableSizes: PropTypes.arrayOf(PropTypes.string),
  categoryName: PropTypes.string.isRequired,
  categoryPath: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      id: PropTypes.string,
    }),
  ).isRequired,
  categoryUrl: PropTypes.string.isRequired,
  countryId: PropTypes.string.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  filterSettings: PropTypes.object.isRequired,
  freeShipping: PropTypes.bool.isRequired,
  handleBrandsFilter: PropTypes.func.isRequired,
  handleCategoriesFilter: PropTypes.func.isRequired,
  handleClearFilters: PropTypes.func.isRequired,
  handleOffset: PropTypes.func.isRequired,
  handlePriceFilter: PropTypes.func.isRequired,
  handleSeasonsFilter: PropTypes.func.isRequired,
  handleSizesFilter: PropTypes.func.isRequired,
  handleSorting: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  offset: PropTypes.number.isRequired,
  productData: PropTypes.object,
  products: PropTypes.array.isRequired,
  query: PropTypes.string,
  s3BasePath: PropTypes.string.isRequired,
  sortFilterSelect: PropTypes.string.isRequired,
  searchProducts: PropTypes.func.isRequired,
  seasons: PropTypes.arrayOf(PropTypes.string),
  sizes: PropTypes.arrayOf(PropTypes.string),
  total: PropTypes.number.isRequired,
  mode: PropTypes.oneOf(allModes).isRequired,
};

ProductList = connect(mapStateToProps, mapDispatchToProps)(ProductList);

export default withCountryCode(ProductList);
